import type { TrackId } from "@src/types";

import { usePlayerState } from "@src/states/player";

import { player } from "@src/services/player";


/**
 * Camada de abstração entre os componentes React e o `player`, dedicada às
 * tracks "overlay".
 *
 * As tracks do sistema se dividem em duas categorias:
 *
 * - BASE track: o áudio de fundo contínuo, do qual há sempre exatamente um
 *   ativo. É a rádio online (`radio`) ou, quando sem conexão, o modo offline
 *   (`offline`). É a referência para a qual todo overlay devolve o áudio ao
 *   terminar. O `player` e a orquestração online/offline cuidam dela; este
 *   service NÃO a gerencia.
 *
 * - OVERLAY track: áudio transitório que toca POR CIMA do base, abaixando o
 *   volume dele (fade out) durante a fala e devolvendo-o (fade in) ao final —
 *   sem nunca substituí-lo. São `tts`, `beep`, `scheduled` e `onDemand`. Vários
 *   podem ser disparados, mas apenas um toca por vez; o base nunca é overlay
 *   e overlay nunca é base. Este service existe só para eles.
 *
 * O fade é exclusivo do base: os overlays tocam sempre em volume normal.
 *
 * Problema que resolve: cada overlay capturava `currentTrackId` de forma
 * independente para saber a quem devolver o áudio. Como nada os serializava,
 * um overlay podia capturar OUTRO overlay como base (ex.: um agendado disparando
 * no meio de um sob demanda), corrompendo a retomada e deixando a rádio muda.
 *
 * Solução: uma fila serializada. Só um overlay roda por vez, do início ao fim
 * (incluindo o fade in de retorno). Assim, quando um overlay começa, o
 * `currentTrackId` é SEMPRE a track base verdadeira — a captura fica correta
 * por construção. O `player` continua agnóstico de tracks; a regra específica
 * de overlay vive aqui.
 */

type Segment = {
  trackId: TrackId;
  url: string;
  /** playbackRate aplicado quando o segmento começa a tocar (ex.: velocidade do TTS). */
  speed?: number;
  /** Revoga a object URL após o uso (blobs de TTS criados via URL.createObjectURL). */
  revokeUrlAfter?: boolean;
  /** Silêncio (ms) antes e depois deste segmento, dentro da janela duckada. */
  padMs?: number;
  /** Dispara uma única vez, quando este segmento efetivamente começa a tocar. */
  onStart?: () => void;
};

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

class PlayerOverlays {
  /** Cauda da fila serializada: cada overlay encadeia após o anterior liquidar. */
  #tail: Promise<unknown> = Promise.resolve();
  /** Quantidade de overlays na fila (aguardando ou tocando). */
  #pending = 0;
  /** Incrementado por clear(): invalida a fila e o overlay em andamento. */
  #epoch = 0;
  /** Aborta o #playUntilEnded em andamento (definido só enquanto um toca). */
  #abortActive: (() => void) | null = null;

  /** Há algum overlay na fila (tocando ou aguardando)? Usado para feedback de UI. */
  get busy(): boolean {
    return this.#pending > 0;
  }

  /**
   * Descarta a fila pendente e aborta o overlay em andamento. Usado no logout,
   * quando a sessão (e as tracks do player) são desmontadas: sem isso, overlays
   * enfileirados/tocando continuariam rodando contra um player já esvaziado.
   */
  clear(): void {
    this.#epoch++; // overlays enfileirados/rodando checam e desistem
    this.#abortActive?.(); // resolve o segmento atual imediatamente
  }

  /** Enfileira uma operação de overlay; devolve a promise dela ao chamador. */
  #enqueue(fn: () => Promise<void>): Promise<void> {
    this.#pending++;
    // Só executa se não foi cancelado enquanto aguardava na fila.
    const epoch = this.#epoch;
    const guarded = (): Promise<void> =>
      epoch === this.#epoch ? fn() : Promise.resolve();
    // Roda após a cauda liquidar (sucesso OU falha), para uma falha não travar a fila.
    const run = this.#tail.then(guarded, guarded);
    this.#tail = run.then(
      () => {},
      () => {},
    );
    run.then(
      () => this.#pending--,
      () => this.#pending--,
    );
    return run;
  }

  /**
   * Toca um track do início ao fim; resolve com o motivo do término.
   *
   * Watchdog de início: se o track não chegar a tocar em `startTimeoutMs`,
   * resolve como "error". Isso protege contra o `play()` ser descartado
   * silenciosamente (ex.: o watchdog do próprio player travando o lock ao
   * resetar a rádio no meio de um overlay) ou o áudio nunca disparar evento —
   * sem isso a fila inteira congelaria (`busy` preso em `true` para sempre). O
   * timer é limpo assim que "playing" dispara, então áudios longos não têm cap.
   */
  #playUntilEnded(
    trackId: TrackId,
    onPlaying?: () => void,
    startTimeoutMs = 10_000,
  ): Promise<"ended" | "error"> {
    return new Promise((resolve) => {
      let settled = false;
      const finish = (result: "ended" | "error") => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        this.#abortActive = null;
        resolve(result);
      };
      const timer = setTimeout(() => finish("error"), startTimeoutMs);
      // Permite que clear() (logout) encerre este segmento na hora.
      this.#abortActive = () => finish("error");

      player.setExtraEventListener(trackId, "playing", () => {
        clearTimeout(timer);
        onPlaying?.();
      });
      player.setExtraEventListener(trackId, "ended", () => finish("ended"));
      player.setExtraEventListener(trackId, "error", () => finish("error"));
      player.play(trackId);
    });
  }

  /**
   * Toca uma sequência de segmentos por cima da track base, estritamente
   * sequencial e sem sobreposição. O fade é exclusivo da base. Retorna o motivo
   * de término de cada segmento (na ordem), ou `[]` se nem chegou a tocar.
   */
  async #runOverlay(
    segments: Segment[],
    onlyIfBasePlaying: boolean,
  ): Promise<("ended" | "error")[]> {
    // Capturado dentro da fila: aqui o currentTrackId é sempre a base real.
    // currentTrack (metadados de exibição) é capturado junto para ser restaurado
    // ao final — durante o overlay, o onStart o sobrescreve com o título do overlay.
    const { currentTrackId: base, currentTrack: baseTrack } =
      usePlayerState.getState();
    const wasPlaying = player.getPlayingOrPaused(base) === "playing";

    // Se clear() (logout) rodar durante este overlay, o epoch muda e desistimos.
    const epoch = this.#epoch;
    const cancelled = () => epoch !== this.#epoch;

    if (onlyIfBasePlaying && !wasPlaying) {
      return [];
    }

    // 1. Fade out da base (o único que recebe fade). hasTrack protege o caso da
    // base ter sido removida (ex.: transição para offline) entre a captura e aqui.
    if (wasPlaying && player.hasTrack(base)) {
      await player.fadeOut(base);
    }

    // 2. Segmentos, um a um, em volume normal. O try/finally garante a limpeza
    // (listeners, track, blob) mesmo se um await lançar no meio.
    const results: ("ended" | "error")[] = [];
    for (const seg of segments) {
      if (cancelled()) break; // não inicia novos segmentos após clear()
      if (seg.padMs) await sleep(seg.padMs); // silêncio antes de tocar
      let result: "ended" | "error" = "error";
      try {
        await player.upsertTrack(seg.trackId, seg.url, false);
        let firstPlaying = true;
        result = await this.#playUntilEnded(seg.trackId, () => {
          if (seg.speed) player.setSpeed(seg.trackId, seg.speed);
          if (firstPlaying) {
            firstPlaying = false;
            seg.onStart?.();
          }
        });
      } catch (error) {
        console.error(
          `[playerOverlays] Falha no segmento ${seg.trackId}:`,
          error,
        );
      } finally {
        // O player não remove os extra listeners no removeTrack; limpamos aqui.
        player.unsetExtraEventListener(seg.trackId, "playing");
        player.unsetExtraEventListener(seg.trackId, "ended");
        player.unsetExtraEventListener(seg.trackId, "error");
        player.removeTrack(seg.trackId);
        if (seg.revokeUrlAfter) URL.revokeObjectURL(seg.url);
      }
      if (seg.padMs && !cancelled()) await sleep(seg.padMs); // silêncio depois
      results.push(result);
    }

    // 3. Restaura a base (id + metadados de exibição) e faz fade in. O fade in é
    // aguardado para que o próximo overlay da fila não comece o fade out no meio
    // dele. hasTrack protege o caso da base ter sumido durante o overlay;
    // cancelled() pula a restauração quando a sessão foi encerrada (logout).
    if (!cancelled() && player.hasTrack(base)) {
      usePlayerState.setState({ currentTrackId: base, currentTrack: baseTrack });
      if (wasPlaying) {
        await player.fadeIn(base, true);
      }
    }

    return results;
  }

  /**
   * Locutor virtual: fala um áudio TTS (opcionalmente precedido de um beep),
   * abaixando a rádio durante a fala. Rejeita se o áudio falhar sem nunca tocar.
   */
  speakTTS(opts: {
    idRadio: string;
    url: string;
    speed: number;
    /** URL do beep da rádio (`radio.tts_beep`); `null` = não tocar beep. */
    beepUrl: string | null;
    /** Dispara quando o TTS começa a falar (após o beep, se houver). */
    onStart?: () => void;
  }): Promise<void> {
    return this.#enqueue(async () => {
      const segments: Segment[] = [];
      if (opts.beepUrl) {
        // Beep tocado direto (same-origin, via proxy `/api`): cai na regra 6 do
        // SW (NetworkOnly), então o HTTP cache do browser cuida do cache e da
        // revalidação por ETag — trocou o beep no admin, a próxima fala já pega
        // a versão nova. Nada de `?preloaded` (cairia no CacheFirst e nunca
        // revalidaria).
        segments.push({ trackId: "beep", url: opts.beepUrl });
      }
      segments.push({
        trackId: "tts",
        url: opts.url,
        speed: opts.speed,
        revokeUrlAfter: true,
        padMs: 500, // respiro de 500ms antes e depois da fala
        onStart: opts.onStart,
      });

      // O TTS é sempre o último segmento; só houve fala se ele tocou até o fim.
      // (Se o beep tocar mas o TTS falhar, isto rejeita e o onStart nunca dispara
      // — evita o toast de sucesso indevido.)
      const results = await this.#runOverlay(segments, false);
      if (results[results.length - 1] !== "ended") {
        throw new Error("O áudio TTS falhou ao reproduzir.");
      }
    });
  }

  /** Áudio sob demanda (não agendado): toca por cima da base ao ser clicado. */
  playOnDemand(opts: {
    url: string;
    onStart?: () => void;
    onEnded?: () => void;
  }): Promise<void> {
    return this.#enqueue(async () => {
      const results = await this.#runOverlay(
        [{ trackId: "onDemand", url: opts.url, onStart: opts.onStart }],
        false,
      );
      if (results[0] === "ended") opts.onEnded?.();
    });
  }

  /**
   * Áudios agendados (cron): toca a fila por cima da base, mas apenas se a base
   * estiver tocando (agendados não interrompem uma rádio pausada).
   */
  playScheduled(opts: {
    audios: { url: string; onStart?: () => void }[];
    onEnded?: () => void;
  }): Promise<void> {
    return this.#enqueue(async () => {
      const segments: Segment[] = opts.audios.map((audio) => ({
        trackId: "scheduled",
        url: audio.url,
        onStart: audio.onStart,
      }));
      // results vazio = pulado por base pausada. Se executou (mesmo com falhas),
      // dispara onEnded para limpar a fila de cron e não retentar em loop.
      const results = await this.#runOverlay(segments, true);
      if (results.length > 0) opts.onEnded?.();
    });
  }
}

export const playerOverlays = new PlayerOverlays();
